import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Claims one topbar action id for the current view, returning the function that releases it.
 *
 * @since 1.0.0
 */
export type ShellActionRegistrar = (actionId: string, handler: () => void) => () => void;

/**
 * Carries the registrar from the shell down to whichever view is routed. It is null outside
 * the shell, which is what lets a page render in a test without one.
 *
 * @since 1.0.0
 */
export const ShellActionContext = createContext<ShellActionRegistrar | null>(null);

/**
 * Wires a topbar button to the view that owns it for as long as the view is mounted.
 *
 * @since 1.0.0
 * @param actionId - the action id declared on the nav item
 * @param handler - what the button should do
 * @returns void
 */
export function useShellAction(actionId: string, handler: () => void): void {
    const register = useContext(ShellActionContext);
    const handlerRef = useRef(handler);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        if (register === null) {
            return;
        }

        return register(actionId, () => {
            handlerRef.current();
        });
    }, [actionId, register]);
}
